import { useQueryClient } from '@tanstack/react-query';
import { Check, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Spinner, useToast } from '@/components/ui';
import { usePatient, usePatientActions } from '@/features/patients/api';
import type {
  AppAccountAnswer,
  AppAccountRef,
  PatientIdentity,
} from '@/features/patients/types';
import { isApiError } from '@/lib/api';
import { appAccountFromProblem } from '@/pages/patients/app-account-format';
import { PatientIdentityText } from '@/pages/patients/PatientIdentityText';
import { AppAccountConfirm } from './AppAccountConfirm';
import confirmStyles from './AppAccountConfirm.module.css';
import styles from './queue.module.css';

const STEPS = [
  { key: 'chip', title: 'MyKad chip read', hint: 'Reader on counter 1' },
  { key: 'face', title: 'Face match', hint: 'Camera on the counter tablet' },
  {
    key: 'confirm',
    title: 'Confirm details with patient',
    hint: 'Name, IC, address as read',
  },
] as const;

/**
 * "Counter eKYC" (Admin v2 #13 and the register panel #23): MyKad chip read,
 * face match, details confirmed, then Mark verified. The eKYC provider is not
 * connected on staging: staff confirm each step; the provider's pass/fail
 * replaces these taps when it is integrated.
 *
 * RL-1118 (security review S1): when the record has an app account, the
 * counter must also ask whether that app account is the patient's; Mark
 * verified stays disabled until staff answer. The record is read first
 * (GET /patients/:id `appAccount`); if that fails, or the link appeared since,
 * the API's 422 APP_ACCOUNT_CONFIRMATION_REQUIRED brings up the same question.
 */
export function EkycSteps({
  patient,
  onVerified,
}: {
  patient: {
    id: string;
    fullName: string;
    identity: PatientIdentity | null;
  } | null;
  onVerified?: () => void;
}) {
  const [done, setDone] = useState(0);
  const [answer, setAnswer] = useState<AppAccountAnswer | null>(null);
  /** Set from 422 APP_ACCOUNT_CONFIRMATION_REQUIRED. */
  const [required, setRequired] = useState<AppAccountRef | null>(null);
  const [failure, setFailure] = useState<{
    message: string;
    requestId?: string;
  } | null>(null);
  const { t } = useTranslation();
  const qc = useQueryClient();
  const a = usePatientActions();
  const toast = useToast();
  const record = usePatient(patient?.id);
  const checking = !!patient && record.isLoading;
  // The API asks only while the record is unverified (a verified record
  // ignores the answer), so a verified record shows no question.
  const unverified = record.data?.ekyc.status !== 'verified';
  const linked: AppAccountRef | null =
    required ?? (unverified ? (record.data?.appAccount ?? null) : null);
  const needsAnswer = !!linked && !answer;

  async function markVerified() {
    if (!patient) return;
    setFailure(null);
    try {
      await a.verifyEkyc.mutateAsync({
        id: patient.id,
        appAccount: linked ? (answer ?? undefined) : undefined,
      });
      toast.show({
        tone: 'success',
        title: 'eKYC verified',
        body:
          linked && answer === 'not_theirs'
            ? 'The app account was unlinked from this record and signed out; the arrival pass is unlocked.'
            : linked
              ? "App account confirmed as the patient's; the app can show this record. The arrival pass is unlocked."
              : 'Written to the same record; the arrival pass is unlocked.',
      });
      onVerified?.();
    } catch (err: unknown) {
      if (isApiError(err) && err.code === 'APP_ACCOUNT_CONFIRMATION_REQUIRED') {
        const fromProblem = appAccountFromProblem(err.problem);
        setRequired(
          fromProblem.method || !record.data?.appAccount
            ? fromProblem
            : record.data.appAccount,
        );
        setAnswer(null);
        setFailure({
          message:
            'This record is linked to an app account. Ask the patient whether it is theirs, choose an answer, then mark verified again.',
          requestId: err.requestId,
        });
        void qc.invalidateQueries({ queryKey: ['patient', patient.id] });
        return;
      }
      setFailure({
        message: isApiError(err)
          ? err.message
          : 'Could not mark verified. Try again.',
        requestId: isApiError(err) ? err.requestId : undefined,
      });
    }
  }

  return (
    <div className={styles.drawer}>
      <ol className={styles.steps} aria-label="Counter eKYC steps">
        {STEPS.map((s, i) => (
          <li key={s.key} className={styles.step}>
            <span
              className={[
                styles.stepDot,
                i < done && styles.stepDone,
                i === done && styles.stepCurrent,
              ]
                .filter(Boolean)
                .join(' ')}
              aria-hidden="true"
            >
              {i < done && <Check size={12} />}
            </span>
            <span>
              <strong>{s.title}</strong>
              <br />
              <span className={styles.sub}>
                {s.key === 'confirm' && patient ? (
                  <>
                    {patient.fullName} ·{' '}
                    <PatientIdentityText identity={patient.identity} />
                  </>
                ) : (
                  s.hint
                )}
              </span>
            </span>
            {i === done && (
              <Button
                size="sm"
                disabled={!patient}
                onClick={() => setDone(i + 1)}
              >
                Done
              </Button>
            )}
          </li>
        ))}
        <li className={styles.step}>
          <span className={styles.stepDot} aria-hidden="true" />
          <span>
            <strong>Marked verified</strong>
            <br />
            <span className={styles.sub}>Arrival pass unlocks in the app</span>
          </span>
        </li>
      </ol>
      {checking && (
        <p className={confirmStyles.status} role="status">
          <Spinner size={14} />
          Checking for a linked app account…
        </p>
      )}
      {record.isError && !linked && (
        <p className={confirmStyles.status} role="status">
          Could not check for a linked app account. If one is linked, you will
          be asked about it when you mark verified.
        </p>
      )}
      {linked && (
        <AppAccountConfirm
          account={linked}
          answer={answer}
          onAnswer={(next) => {
            setAnswer(next);
            setFailure(null);
          }}
          disabled={a.verifyEkyc.isPending}
        />
      )}
      {failure && (
        <p className={confirmStyles.alert} role="alert">
          {failure.message}
          {failure.requestId && (
            <span className={confirmStyles.requestId}>
              {t('common.requestId', { id: failure.requestId })}
            </span>
          )}
        </p>
      )}
      <Button
        variant="primary"
        icon={<ShieldCheck size={16} />}
        disabled={!patient || done < STEPS.length || checking || needsAnswer}
        loading={a.verifyEkyc.isPending}
        onClick={() => void markVerified()}
      >
        {linked && answer === 'not_theirs'
          ? 'Mark verified and unlink app account'
          : 'Mark verified'}
      </Button>
      {needsAnswer && done >= STEPS.length && (
        <p className={styles.sub}>
          Answer whether the app account is the patient&apos;s first.
        </p>
      )}
      <p className={styles.sub}>
        Staging: the eKYC provider is not connected, so each step is confirmed
        by the counter.
      </p>
    </div>
  );
}

export function CounterEkycDrawer({
  patient,
  onClose,
}: {
  patient: {
    id: string;
    fullName: string;
    identity: PatientIdentity | null;
  } | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!patient} onClose={onClose} title="Counter eKYC">
      <div className={styles.drawer}>
        <p className={styles.errorNote}>
          Same provider, same result written to the same record. Takes under a
          minute.
        </p>
        <EkycSteps key={patient?.id} patient={patient} onVerified={onClose} />
      </div>
    </Dialog>
  );
}
